/**
 * GeneratorConfiguration - Multi-generator configuration component
 *
 * Supports multiple generators with mix-and-match from database.
 * Aggregates by fuel type for solver: diesel → Generator, natural_gas → CHP
 * TODO: Future - individual dispatch control per generator
 *
 * Supports two modes:
 * 1. Props mode (legacy): Pass individual values and setters as props
 * 2. Context mode (new): Uses React Hook Form context when available
 *
 * The component automatically detects which mode to use based on whether
 * it's wrapped in a FormProvider and whether props are provided.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Chip,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { vortxColors, useEquipmentDatabase } from '@vortx/shared-ui';
import { useFormField, useFormArrayField } from './useFormField';

const SIZING_MODES = [
  { value: 'prescribed', label: 'Prescribed Size', description: 'Specify exact generator size' },
  { value: 'optimal', label: 'Optimize Size', description: 'Let REopt find optimal generator size' },
];

const FUEL_TYPES = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'natural_gas', label: 'Natural Gas' },
];

const EXPORT_MODES = [
  { value: 'none', label: 'No Export' },
  { value: 'net_meter', label: 'Net Metering' },
  { value: 'wholesale', label: 'Wholesale' },
];

/**
 * GeneratorConfiguration Component
 *
 * @param {Object} props
 * @param {boolean} [props.useGenerator] - Whether generator is enabled (props mode)
 * @param {Function} [props.setUseGenerator] - Generator enabled setter (props mode)
 * @param {string} [props.sizingMode] - 'optimal' or 'prescribed' (props mode)
 * @param {Function} [props.setSizingMode] - Sizing mode setter (props mode)
 * @param {string} [props.generatorMaxKw] - Max generator kW (props mode, legacy single-generator)
 * @param {Function} [props.setGeneratorMaxKw] - Max kW setter (props mode)
 * @param {string} [props.generatorFuelType] - Fuel type (props mode, legacy)
 * @param {Function} [props.setGeneratorFuelType] - Fuel type setter (props mode)
 * @param {string} [props.generatorExportMode] - Export mode (props mode)
 * @param {Function} [props.setGeneratorExportMode] - Export mode setter (props mode)
 * @param {string} [props.generatorRunHoursLimit] - Run hours limit (props mode)
 * @param {Function} [props.setGeneratorRunHoursLimit] - Run hours limit setter (props mode)
 * @param {string} [props.generatorUptimePercent] - Uptime percent (props mode)
 * @param {Function} [props.setGeneratorUptimePercent] - Uptime setter (props mode)
 * @param {string} [props.generatorThermalEfficiency] - Thermal efficiency (props mode)
 * @param {Function} [props.setGeneratorThermalEfficiency] - Efficiency setter (props mode)
 * @param {Array} [props.generators] - Array of generator configs (props mode)
 * @param {Function} [props.setGenerators] - Generators array setter (props mode)
 * @param {Function} [props.onGeneratorSelect] - Callback for generator selection
 * @param {Object} [props.vortxStyles] - Styling object
 */
function GeneratorConfiguration({
  // Props mode values (backward compatible)
  useGenerator: propUseGenerator,
  setUseGenerator,
  sizingMode: propSizingMode = 'prescribed',
  setSizingMode,
  // Legacy single-generator props (kept for backward compatibility)
  generatorMaxKw: propGeneratorMaxKw,
  setGeneratorMaxKw,
  generatorFuelType: propGeneratorFuelType,
  setGeneratorFuelType,
  generatorExportMode: propGeneratorExportMode,
  setGeneratorExportMode,
  generatorRunHoursLimit: propGeneratorRunHoursLimit,
  setGeneratorRunHoursLimit,
  generatorUptimePercent: propGeneratorUptimePercent,
  setGeneratorUptimePercent,
  generatorThermalEfficiency: propGeneratorThermalEfficiency,
  setGeneratorThermalEfficiency,
  // Multi-generator props
  generators: propGenerators = [],
  setGenerators,
  onGeneratorSelect,
  vortxStyles = {},
}) {
  // Fetch generator equipment from database
  const { data: generatorDatabase = [], loading: loadingGenerators } = useEquipmentDatabase('generator');

  // Get form fields (automatically uses context or props)
  const enabled = useFormField('generator.enabled', propUseGenerator, setUseGenerator);
  const sizingMode = useFormField('generator.sizingMode', propSizingMode, setSizingMode);
  const maxKw = useFormField('generator.maxKw', propGeneratorMaxKw, setGeneratorMaxKw);
  const fuelType = useFormField('generator.fuelType', propGeneratorFuelType, setGeneratorFuelType);
  const exportMode = useFormField('generator.exportMode', propGeneratorExportMode, setGeneratorExportMode);
  const runHoursLimit = useFormField('generator.runHoursLimit', propGeneratorRunHoursLimit, setGeneratorRunHoursLimit);
  const uptimePercent = useFormField('generator.uptimePercent', propGeneratorUptimePercent, setGeneratorUptimePercent);
  const thermalEfficiency = useFormField('generator.thermalEfficiency', propGeneratorThermalEfficiency, setGeneratorThermalEfficiency);
  const generators = useFormArrayField('generator.generators', propGenerators, setGenerators);

  // Local state for input mode
  const [inputMode, setInputMode] = useState('database'); // 'database' or 'manual'

  // Calculate total capacity by fuel type
  const totalsByFuel = useMemo(() => {
    const totals = { diesel: 0, natural_gas: 0 };
    generators.value.forEach(g => {
      if (g.fuel_type && g.kw) {
        totals[g.fuel_type] = (totals[g.fuel_type] || 0) + parseFloat(g.kw);
      }
    });
    return totals;
  }, [generators.value]);

  const totalCapacity = useMemo(() => {
    return generators.value.reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0);
  }, [generators.value]);

  // Add new generator
  const handleAddGenerator = () => {
    const newId = Math.max(0, ...generators.value.map(g => g.id)) + 1;
    generators.append({
      id: newId,
      name: `Generator ${newId}`,
      kw: '',
      fuel_type: 'diesel',
      database_id: null,
      manufacturer: '',
      model: '',
      efficiency: '',
    });
  };

  // Remove generator
  const handleRemoveGenerator = (id) => {
    generators.remove(id);
  };

  // Update generator field
  const handleUpdateGenerator = (id, field, value) => {
    generators.update(id, field, value);
  };

  // Handle database selection for a specific generator
  const handleDatabaseSelect = (generatorId, dbGenerator) => {
    if (dbGenerator) {
      const updatedGenerators = generators.value.map(g =>
        g.id === generatorId ? {
          ...g,
          database_id: dbGenerator.id,
          kw: String(dbGenerator.power_kw),
          fuel_type: dbGenerator.fuel_type,
          manufacturer: dbGenerator.manufacturer,
          model: dbGenerator.model,
          efficiency: dbGenerator.fuel_type === 'diesel'
            ? dbGenerator.fuel_efficiency_kwh_per_gal
            : dbGenerator.fuel_efficiency_kwh_per_mmbtu,
        } : g
      );
      generators.setValue(updatedGenerators);
    }
  };

  return (
    <Box sx={{
      border: `1px solid rgba(178, 140, 80, 0.2)`,
      borderRadius: 2,
      overflow: 'hidden',
      mb: 2
    }}>
      {/* Header with toggle */}
      <Box sx={{
        p: 2,
        bgcolor: enabled.value ? alpha(vortxColors.gold, 0.1) : 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(enabled.value)}
              onChange={(e) => enabled.setValue(e.target.checked)}
              sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }}
            />
          }
          label={
            <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
              Generator / Genset
            </Typography>
          }
        />
        {enabled.value && totalCapacity > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${totalCapacity.toLocaleString()} kW Total`}
              size="small"
              sx={{ bgcolor: alpha(vortxColors.gold, 0.15), color: vortxColors.charcoal }}
            />
            {totalsByFuel.diesel > 0 && (
              <Chip
                label={`${totalsByFuel.diesel.toLocaleString()} kW Diesel`}
                size="small"
                variant="outlined"
                sx={{ borderColor: vortxColors.gold, color: 'text.secondary' }}
              />
            )}
            {totalsByFuel.natural_gas > 0 && (
              <Chip
                label={`${totalsByFuel.natural_gas.toLocaleString()} kW NG`}
                size="small"
                variant="outlined"
                sx={{ borderColor: vortxColors.gold, color: 'text.secondary' }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Configuration section */}
      {enabled.value && (
        <Box sx={{ p: 2, borderTop: `1px solid rgba(178, 140, 80, 0.2)` }}>
          {/* Sizing Mode */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
              Sizing Mode
            </FormLabel>
            <RadioGroup
              row
              value={sizingMode.value || 'prescribed'}
              onChange={(e) => sizingMode.setValue(e.target.value)}
            >
              {SIZING_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.value}
                  value={mode.value}
                  control={<Radio sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{mode.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{mode.description}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Input Mode Toggle */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', mb: 1, fontWeight: 500 }}>
              Generator Selection
            </Typography>
            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={(e, mode) => mode && setInputMode(mode)}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="database" sx={{ px: 2 }}>
                Select from Database
              </ToggleButton>
              <ToggleButton value="manual" sx={{ px: 2 }}>
                Manual Input
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Generator List */}
          <Box sx={{ mb: 2 }}>
            {generators.value.map((gen, index) => (
              <Box
                key={gen.id}
                sx={{
                  p: 2,
                  mb: 1,
                  bgcolor: alpha(vortxColors.gold, 0.05),
                  borderRadius: 1,
                  border: `1px solid rgba(178, 140, 80, 0.2)`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Generator {index + 1}
                    {gen.manufacturer && gen.model && (
                      <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary' }}>
                        — {gen.manufacturer} {gen.model}
                      </Typography>
                    )}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGenerator(gen.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Fuel Type */}
                  <TextField
                    select
                    label="Fuel Type"
                    value={gen.fuel_type || 'diesel'}
                    onChange={(e) => handleUpdateGenerator(gen.id, 'fuel_type', e.target.value)}
                    size="small"
                    sx={{ minWidth: 140 }}
                  >
                    {FUEL_TYPES.map((fuel) => (
                      <MenuItem key={fuel.value} value={fuel.value}>
                        {fuel.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {inputMode === 'database' ? (
                    /* Database Selection */
                    <Autocomplete
                      size="small"
                      options={generatorDatabase.filter(g => g.fuel_type === gen.fuel_type)}
                      getOptionLabel={(option) => `${option.manufacturer} ${option.model} (${option.power_kw} kW)`}
                      value={generatorDatabase.find(g => g.id === gen.database_id) || null}
                      onChange={(e, dbGen) => handleDatabaseSelect(gen.id, dbGen)}
                      loading={loadingGenerators}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Generator" placeholder="Search..." />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <Box component="li" key={key} {...otherProps}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.manufacturer} {option.model}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {option.power_kw} kW • ${option.capex_per_kw}/kW
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      sx={{ minWidth: 280, flex: 1 }}
                    />
                  ) : (
                    /* Manual Input */
                    <>
                      <TextField
                        label="Capacity (kW)"
                        type="number"
                        value={gen.kw || ''}
                        onChange={(e) => handleUpdateGenerator(gen.id, 'kw', e.target.value)}
                        size="small"
                        inputProps={{ min: 0 }}
                        sx={{ width: 130 }}
                      />
                      <TextField
                        label={gen.fuel_type === 'diesel' ? 'Efficiency (kWh/gal)' : 'Efficiency (kWh/MMBtu)'}
                        type="number"
                        value={gen.efficiency || ''}
                        onChange={(e) => handleUpdateGenerator(gen.id, 'efficiency', e.target.value)}
                        size="small"
                        placeholder={gen.fuel_type === 'diesel' ? '10.0' : '121.5'}
                        sx={{ width: 160 }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            ))}

            {/* Add Generator Button */}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddGenerator}
              sx={{
                mt: 1,
                color: vortxColors.gold,
                borderColor: vortxColors.gold,
                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) }
              }}
              variant="outlined"
              size="small"
            >
              Add Generator
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Global Settings */}
          <Typography variant="body2" sx={{ color: 'text.primary', mb: 1, fontWeight: 500 }}>
            Global Settings
          </Typography>

          {/* Run Hours Limit */}
          <TextField
            label="Run Hours Limit (annual)"
            type="number"
            value={runHoursLimit.value || ''}
            onChange={(e) => runHoursLimit.setValue(e.target.value)}
            inputProps={{ min: 0, max: 8760 }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            sx={{ ...vortxStyles.textField, mb: 2 }}
            helperText="Maximum annual run hours (leave blank for no limit)"
            placeholder="8760"
            error={!!runHoursLimit.error}
          />

          {/* Uptime/Availability */}
          <TextField
            label="Uptime / Availability (%)"
            type="number"
            value={uptimePercent.value || ''}
            onChange={(e) => uptimePercent.setValue(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            sx={{ ...vortxStyles.textField, mb: 2 }}
            helperText="Generator availability factor (accounts for maintenance). Default: 96.5%"
            placeholder="96.5"
            error={!!uptimePercent.error}
          />

          {/* Export Mode */}
          <TextField
            select
            label="Export Mode"
            value={exportMode.value || 'none'}
            onChange={(e) => exportMode.setValue(e.target.value)}
            fullWidth
            size="small"
            sx={vortxStyles.textField}
            error={!!exportMode.error}
          >
            {EXPORT_MODES.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                {mode.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Box>
  );
}

// Export constants for use elsewhere
export { SIZING_MODES, FUEL_TYPES, EXPORT_MODES };

export default GeneratorConfiguration;
